import React, { useState, useEffect, useMemo } from 'react';
import './ScheduleView.css';
import { config } from '../../config';
import { ScheduleEventItem } from './ScheduleEventItem';
import { InfoBanner } from './InfoBanner';
import { fetchScheduleData } from '../../services/scheduleDataService';
import { extractAllDates, groupEventsByDate } from '../../services/dataTransformers';
import { OlympicsListableEvent } from '../../types/olympicsListableEvent';
import { transformToListableEvents } from '../../transformers/olympicsEventTransformer';
import chevronLeft from './assets/chevron-left.svg';
import chevronRight from './assets/chevron-right.svg';
import chevronDown from './assets/chevron-down.svg';
import { trackWidgetShown, trackWidgetNav } from '../../utils/analytics';
import { getOlympicYearFromURL } from '../../utils/urlUtils';

type FilterType = 'finns' | 'all';

function formatDateKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function isSameDay(date1: Date, date2: Date): boolean {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

function isNotResultlessFinishedEvent(event: OlympicsListableEvent): boolean {
  if (event.progression !== 'finished') return true; // Always show non-finished events

  if (event.sportsType === 'individual') {
    return (
      event.results !== null &&
      (event.results.bestThree.length > 0 || event.results.finnsBelowThree.length > 0)
    );
  }

  if (event.sportsType === 'team') {
    return event.participants !== null;
  }

  // sportsType === 'not_known' - hide finished events with no results
  return false;
}

export default function ScheduleView() {
  const [activeFilter, setActiveFilter] = useState<FilterType>('finns');
  const [allDates, setAllDates] = useState<Date[]>([]);
  const [eventsData, setEventsData] = useState<Map<string, OlympicsListableEvent[]>>(new Map());
  const [currentDateIndex, setCurrentDateIndex] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasSetInitialFilter, setHasSetInitialFilter] = useState(false);
  const [hasUserResponded, setHasUserResponded] = useState(false);

  // Parse "height_variant" from URL parameter
  const heightVariant = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const variant = params.get('height_variant');
    if (variant === 'live_article' || variant === 'regular_article') {
      return variant;
    }
    return 'regular_article'; // Default to regular_article
  }, []);

  // Parse "brand" from URL parameter
  const brand = useMemo<'hs' | 'is'>(() => {
    const params = new URLSearchParams(window.location.search);
    const brandParam = params.get('brand');
    if (brandParam === 'is') {
      return 'is';
    }
    return 'hs'; // Default to hs
  }, []);

  // Parse "today" from URL parameter, or use current date
  const today = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const dateParam = params.get('date');
    if (dateParam) {
      try {
        const [year, month, day] = dateParam.split('-').map(Number);
        if (year && month && day) {
          return new Date(year, month - 1, day);
        }
      } catch {
        // Fall through to default
      }
    }
    return new Date(); // Current date
  }, []);

  // Get Olympic year from URL parameters
  const olympicYear = useMemo(() => getOlympicYearFromURL(), []);

  // Generate random number once for the widget (stays constant)
  const feedbackFormRandomValue = useMemo(() => {
    const randomValue = Math.random();
    const threshold = config.feedbackFormThreshold;
    const willShow = randomValue < threshold;
    console.log(
      `Insight roll dice roll is ${randomValue.toFixed(4)}, threshold ${threshold.toFixed(2)}, so form decision is ${willShow ? 'yes' : 'no'}`,
    );
    return randomValue;
  }, []);
  // Randomly determine if feedback form should be shown (threshold from config)
  // Only show feedback form if user hasn't responded yet AND random probability passes
  const shouldShowFeedbackForm =
    !hasUserResponded && feedbackFormRandomValue < config.feedbackFormThreshold;

  // Callback when user responds to feedback
  const handleFeedbackResponse = () => {
    setHasUserResponded(true);
  };

  // Pre-calculate relative dates for label formatting
  const { yesterday, tomorrow } = useMemo(() => {
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return { yesterday, tomorrow };
  }, [today]);

  // Format date label: "Eilen", "Tänään", "Huomenna", or "Ma D.M."
  const formatDateLabel = (date: Date): string => {
    if (isSameDay(date, yesterday)) return 'Eilen';
    if (isSameDay(date, today)) return 'Tänään';
    if (isSameDay(date, tomorrow)) return 'Huomenna';

    // Get weekday abbreviation
    const weekdays = ['Su', 'Ma', 'Ti', 'Ke', 'To', 'Pe', 'La'];
    const weekday = weekdays[date.getDay()];

    return `${weekday} ${date.getDate()}.${date.getMonth() + 1}.`;
  };

  useEffect(() => {
    fetchScheduleData()
      .then((data) => {
        const dates = extractAllDates(data);
        const grouped = groupEventsByDate(data);

        // Transform backend events to frontend format, to make the
        // frontend code cleaner and more straighforward
        // transformToListableEvents filters out events with invalid statusType
        const transformedGrouped = new Map<string, OlympicsListableEvent[]>();
        grouped.forEach((events, dateKey) => {
          transformedGrouped.set(dateKey, transformToListableEvents(events));
        });

        setAllDates(dates);
        setEventsData(transformedGrouped);

        const todayIndex = dates.findIndex((date) => isSameDay(date, today));
        // If the current date is not found in the list, it means the games have already ended --> show last day by default.
        setCurrentDateIndex(todayIndex !== -1 ? todayIndex : dates.length - 1);

        setIsLoading(false);
        trackWidgetShown();
      })
      .catch((error) => {
        console.error('Error fetching data:', error);
        setError('Datan lataaminen epäonnistui');
        setIsLoading(false);
      });
  }, [today]);

  // Set initial filter to 'all' if there are no Finnish events on the initial date
  useEffect(() => {
    if (!hasSetInitialFilter && !isLoading && allDates.length > 0 && eventsData.size > 0) {
      const currentDateKey = formatDateKey(allDates[currentDateIndex]);
      const events = eventsData.get(currentDateKey) || [];
      // Don't count opening ceremony (id: 9999999) as a Finnish event for initial tab selection
      const finnishEvents = events.filter((event) => event.metadata.hasFin && event.id !== 9999999);

      if (finnishEvents.length === 0) {
        setActiveFilter('all');
      }

      setHasSetInitialFilter(true);
    }
  }, [hasSetInitialFilter, isLoading, allDates, eventsData, currentDateIndex]);

  // Calculate the 3-date sliding window with their indices
  const visibleDates = useMemo(() => {
    if (allDates.length === 0) return [];

    const items: Array<{ date: Date | null; index: number }> = [];

    // Add left date (if exists)
    if (currentDateIndex > 0) {
      items.push({ date: allDates[currentDateIndex - 1], index: currentDateIndex - 1 });
    } else {
      items.push({ date: null, index: -1 });
    }

    // Add current date
    items.push({ date: allDates[currentDateIndex], index: currentDateIndex });

    // Add right date (if exists)
    if (currentDateIndex < allDates.length - 1) {
      items.push({ date: allDates[currentDateIndex + 1], index: currentDateIndex + 1 });
    } else {
      items.push({ date: null, index: -1 });
    }

    return items;
  }, [allDates, currentDateIndex]);

  // Get filtered events for the currently selected date
  const currentEvents = useMemo(() => {
    if (allDates.length === 0) return [];

    const currentDateKey = formatDateKey(allDates[currentDateIndex]);
    const events = eventsData.get(currentDateKey) || [];

    // Filter by Finnish participation
    let filteredEvents = events;
    if (activeFilter === 'finns') {
      // Show Finnish events + opening ceremony (id: 9999999) in Suomalaiset view
      filteredEvents = events.filter((event) => event.metadata.hasFin || event.id === 9999999);
    }

    // Filter out finished events with no results (issue #224)
    return filteredEvents.filter((event) => isNotResultlessFinishedEvent(event));
  }, [allDates, currentDateIndex, eventsData, activeFilter]);

  // Check if there are actual Finnish events (excluding opening ceremony)
  const hasActualFinnishEvents = useMemo(() => {
    if (allDates.length === 0) return false;

    const currentDateKey = formatDateKey(allDates[currentDateIndex]);
    const events = eventsData.get(currentDateKey) || [];
    // Count only Finnish events, not opening ceremony
    const finnishEvents = events.filter((event) => event.metadata.hasFin && event.id !== 9999999);
    return finnishEvents.length > 0;
  }, [allDates, currentDateIndex, eventsData]);

  // Check if current date is today or in the future (should show info banner)
  const shouldShowInfoBanner = useMemo(() => {
    if (allDates.length === 0) return false;
    const currentDate = allDates[currentDateIndex];
    // Show banner only for today or future dates (not yesterday or older) and only on Suomalaiset tab
    return currentDate.getTime() >= today.getTime() && activeFilter === 'finns';
  }, [allDates, currentDateIndex, today, activeFilter]);

  // Check if current date is yesterday or older (for showing "Ei suomalaisia" banner)
  const isYesterdayOrOlder = useMemo(() => {
    if (allDates.length === 0) return false;
    const currentDate = allDates[currentDateIndex];
    // Return true only for dates before today
    return currentDate.getTime() < today.getTime();
  }, [allDates, currentDateIndex, today]);

  const canGoLeft = currentDateIndex > 0;
  const canGoRight = currentDateIndex < allDates.length - 1;

  // Trigger iframe resize when content changes
  useEffect(() => {
    const triggerResize = () => {
      const height = document.documentElement.scrollHeight;

      // Get iframe ID from iframe-resizer
      const iframeId = window.parentIFrame?.getId();

      // Send message in iframe-resizer's expected format
      // This mimics what iframe-resizer does internally
      if (window.parent !== window) {
        window.parent.postMessage(
          `[iFrameSizer]${iframeId}:${height}:${document.body.scrollWidth}:resize`,
          '*',
        );
      }

      // Also try the standard API
      if (window.parentIFrame) {
        window.parentIFrame.resize();
      }
    };

    // Wait for DOM to fully render
    const timer = setTimeout(() => {
      requestAnimationFrame(triggerResize);
    }, 100);

    return () => clearTimeout(timer);
  }, [currentDateIndex, isExpanded, activeFilter, currentEvents.length]);

  return (
    <div className={`schedule-view-container ${brand === 'is' ? 'brand-is' : 'brand-hs'}`}>
      <div className={`schedule-content ${isExpanded ? 'expanded' : 'collapsed'} ${heightVariant}`}>
        <h1>Katso aikataulu ja tulokset</h1>

        <div className='date-nav'>
          <button
            className='nav-arrow left'
            onClick={() => {
              if (canGoLeft) {
                trackWidgetNav('date_back_one_day');
                setCurrentDateIndex(currentDateIndex - 1);
              }
            }}
            disabled={!canGoLeft}
            aria-label='Edellinen päivä'
          >
            <img
              alt=''
              className='icon'
              src={chevronLeft}
              style={{ width: '0.5rem', height: '0.75rem' }}
            />
          </button>

          {visibleDates.map(({ date, index }, i) => {
            if (!date) {
              return <div key={`empty-${i}`} className='date-nav-item' />;
            }

            const isActive = index === currentDateIndex;
            const label = formatDateLabel(date);

            return (
              <button
                key={index}
                className={`date-nav-item ${isActive ? 'active' : ''}`}
                onClick={() => setCurrentDateIndex(index)}
              >
                {label}
              </button>
            );
          })}

          <button
            className='nav-arrow right'
            onClick={() => {
              if (canGoRight) {
                trackWidgetNav('date_forward_one_day');
                setCurrentDateIndex(currentDateIndex + 1);
              }
            }}
            disabled={!canGoRight}
            aria-label='Seuraava päivä'
          >
            <img
              alt=''
              className='icon'
              src={chevronRight}
              style={{ width: '0.5rem', height: '0.75rem' }}
            />
          </button>
        </div>

        <div className='filter-buttons'>
          <div className='filter-container'>
            <button
              className={`filter-segment ${activeFilter === 'finns' ? 'selected' : ''}`}
              onClick={() => setActiveFilter('finns')}
              aria-pressed={activeFilter === 'finns'}
            >
              Suomalaiset
            </button>
            <button
              className={`filter-segment ${activeFilter === 'all' ? 'selected' : ''}`}
              onClick={() => {
                trackWidgetNav('filter_nationalities_all');
                setActiveFilter('all');
              }}
              aria-pressed={activeFilter === 'all'}
            >
              Kaikki
            </button>
          </div>
        </div>

        <div className='collapsible-wrapper'>
          <div className='collapsible-content'>
            <div className='event-list'>
              {isLoading ? (
                <div className='event-item'>
                  <div className='event-title'>Ladataan tapahtumia...</div>
                </div>
              ) : error ? (
                <div className='event-item'>
                  <div className='event-title'>{error}</div>
                </div>
              ) : (
                <>
                  {/* Show events list or "no events" message */}
                  {currentEvents.length === 0
                    ? activeFilter === 'all' && (
                        <div className='event-item'>
                          <div className='event-title'>Ei tapahtumia valitulle päivälle.</div>
                        </div>
                      )
                    : currentEvents.map((event, index) => {
                        // Show AI disclaimer only for the first event with insight
                        const firstInsightIndex = currentEvents.findIndex(
                          (e) => e.metadata.insight !== null,
                        );
                        const showAIDisclaimer = index === firstInsightIndex;
                        const isFirstInsight = index === firstInsightIndex;

                        return (
                          <ScheduleEventItem
                            key={event.id.toString()}
                            event={event}
                            year={olympicYear}
                            showAIDisclaimer={showAIDisclaimer}
                            showFeedbackForm={shouldShowFeedbackForm && isFirstInsight}
                            onFeedbackResponse={handleFeedbackResponse}
                          />
                        );
                      })}
                  {/* Show "no Finnish events" banner if on Suomalaiset tab with no actual Finnish events (after events list) */}
                  {activeFilter === 'finns' && !hasActualFinnishEvents && isYesterdayOrOlder && (
                    <div className='event-item'>
                      <div className='event-title'>
                        Ei suomalaisia osallistujia. Katso päivän tulokset Kaikki-välilehdeltä.
                      </div>
                    </div>
                  )}
                </>
              )}
              {shouldShowInfoBanner && !isLoading && !error && <InfoBanner />}
            </div>

            <div className='disclaimer'>
              <p>
                {config.featureFlags.enableInsights && (
                  <>
                    Kisatapahtumista kertovat tiivistelmät on tehty tekoälyllä{' '}
                    {config.featureFlags.enableReutersInsight
                      ? 'IS:n, HS:n ja Reutersin'
                      : 'IS:n ja HS:n'}{' '}
                    uutisista.{' '}
                    <a href={config.aiInfoArticleUrl} target='_blank' rel='noopener noreferrer'>
                      Kerro virheestä tai anna muuta palautetta
                    </a>
                    .
                  </>
                )}{' '}
                Aikataulut ja tulokset: Enetpulse.
              </p>
            </div>
          </div>
          {!isExpanded && <div className='gradient-overlay' />}
        </div>

        <button
          className='toggle-button'
          onClick={() => {
            if (!isExpanded) {
              const eventName = heightVariant === 'live_article' ? 'show_more_live' : 'show_more';
              trackWidgetNav(eventName);
            }
            setIsExpanded(!isExpanded);
          }}
          aria-expanded={isExpanded}
        >
          <span>{isExpanded ? 'Sulje' : 'Näytä lisää'}</span>
          <img
            alt={isExpanded ? 'Sulje' : 'Näytä lisää'}
            src={chevronDown}
            style={{
              width: '0.75rem',
              height: '0.5rem',
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        </button>
      </div>
    </div>
  );
}
